import React, { useState, useEffect } from "react";
import "./DynamicUsaPrCardTable.css";

interface UsaPrCardEntry {
  documentNumber: string;
  expiryDate: string;
}

interface DynamicUsaPrCardTableProps {
  value: { [key: string]: UsaPrCardEntry };
  onChange: (entries: { [key: string]: UsaPrCardEntry }) => void;
  required?: boolean;
  hasUsaPrCard?: string; // To control visibility
}

const DynamicUsaPrCardTable: React.FC<DynamicUsaPrCardTableProps> = ({
  value,
  onChange,
  required = false,
  hasUsaPrCard = "No",
}) => {
  const [entries, setEntries] = useState<{ [key: string]: UsaPrCardEntry }>(
    value || {}
  );

  useEffect(() => {
    onChange(entries);
  }, [entries, onChange]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "Enter") {
        addEntry();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const addEntry = () => {
    const entryCount = Object.keys(entries).length;
    const newKey = `usaPrCard${entryCount + 1}`;

    const newEntry: UsaPrCardEntry = {
      documentNumber: "",
      expiryDate: "",
    };

    const updatedEntries = { ...entries, [newKey]: newEntry };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const removeEntry = (key: string) => {
    const { [key]: removed, ...remainingEntries } = entries;

    // Renumber the remaining entries
    const renumberedEntries: { [key: string]: UsaPrCardEntry } = {};
    const remainingKeys = Object.keys(remainingEntries).sort();

    remainingKeys.forEach((oldKey, index) => {
      const newKey = `usaPrCard${index + 1}`;
      renumberedEntries[newKey] = remainingEntries[oldKey];
    });

    setEntries(renumberedEntries);
    onChange(renumberedEntries);
  };

  const updateEntry = (
    key: string,
    field: keyof UsaPrCardEntry,
    value: string
  ) => {
    const updatedEntries = {
      ...entries,
      [key]: { ...entries[key], [field]: value },
    };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  // Initialize with at least one entry if hasUsaPrCard is "Yes" and no entries exist
  useEffect(() => {
    if (hasUsaPrCard === "Yes" && Object.keys(entries).length === 0) {
      addEntry();
    }
  }, [hasUsaPrCard]);

  // Hide the table if hasUsaPrCard is not "Yes"
  if (hasUsaPrCard !== "Yes") {
    return null;
  }

  const entryKeys = Object.keys(entries).sort();

  return (
    <div className="usa-pr-card-table">
      <div className="table-header">
        <h4>USA/PR Card Details</h4>
        <button
          type="button"
          className="add-btn"
          onClick={addEntry}
          title="Add USA/PR card entry (Ctrl+Enter)"
        >
          + Add USA/PR Card Entry
        </button>
      </div>

      {entryKeys.length === 0 ? (
        <div className="empty-state">
          <p>No USA/PR card information entered yet.</p>
          <button type="button" className="add-first-btn" onClick={addEntry}>
            + Add USA/PR Card Entry
          </button>
        </div>
      ) : (
        <div className="entries-container">
          {entryKeys.map((key, index) => (
            <div key={key} className="entry-card">
              <div className="entry-header">
                <div className="entry-number">
                  <span className="entry-badge">{index + 1}</span>
                  <h5>USA/PR Card Entry {index + 1}</h5>
                </div>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeEntry(key)}
                  title="Remove this USA/PR card entry"
                >
                  ×
                </button>
              </div>

              <div className="entry-form">
                <div className="form-row form-row-1">
                  <div className="field-group">
                    <label>Document Number</label>
                    <input
                      type="text"
                      value={entries[key].documentNumber}
                      onChange={(e) =>
                        updateEntry(key, "documentNumber", e.target.value)
                      }
                      placeholder="e.g., A123456789"
                    />
                  </div>

                  <div className="field-group">
                    <label>Expiry Date</label>
                    <input
                      type="date"
                      value={entries[key].expiryDate}
                      onChange={(e) =>
                        updateEntry(key, "expiryDate", e.target.value)
                      }
                      placeholder="yyyy-mm-dd"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {entryKeys.length > 0 && (
        <div className="table-footer">
          <p className="hint-text">Need to add more USA/PR card entries?</p>
          <button
            type="button"
            className="add-btn"
            onClick={addEntry}
            title="Add another USA/PR card entry (Ctrl+Enter)"
          >
            + Add Another USA/PR Card Entry
          </button>
        </div>
      )}
    </div>
  );
};

export default DynamicUsaPrCardTable;
